import { zodResolver } from "@hookform/resolvers/zod";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { authService } from "../../../services/shared/auth";

const resetPasswordSchema = z
  .object({
    email: z.string().email("Email inválido"),
    codigo: z.string().min(6, "El código debe tener al menos 6 caracteres"),
    nuevaPassword: z
      .string()
      .min(6, "La contraseña debe tener al menos 6 caracteres"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.nuevaPassword === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const email = watch("email");
  const codigo = watch("codigo");

  const handleVerifyCode = async () => {
    if (!email || !codigo) {
      setError("Por favor completa el email y el código");
      return;
    }

    try {
      setError(null);
      setVerifyingCode(true);

      const result = await authService.verifyResetCode(email, codigo);

      if (result.status === "success") {
        setError(null);
        // El código es válido, continuar con el cambio de contraseña
      } else {
        setError(result.message || "Código inválido o expirado");
      }
    } catch (error: any) {
      setError(error.message || "Error al verificar el código");
    } finally {
      setVerifyingCode(false);
    }
  };

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      setError(null);
      setIsSubmitting(true);

      // Primero verificar el código
      const verifyResult = await authService.verifyResetCode(
        data.email,
        data.codigo
      );

      if (verifyResult.status !== "success") {
        setError(verifyResult.message || "Código inválido o expirado");
        setIsSubmitting(false);
        return;
      }

      // Si el código es válido, cambiar la contraseña
      const result = await authService.resetPassword(
        data.email,
        data.codigo,
        data.nuevaPassword
      );

      if (result.status === "success") {
        setSuccess(true);
      } else {
        setError(result.message || "Error al cambiar la contraseña");
      }
    } catch (error: any) {
      setError(error.message || "Error al cambiar la contraseña");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8 text-center">
          <div className="mb-4">
            <svg
              className="mx-auto h-16 w-16 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Contraseña actualizada
          </h2>
          <p className="text-gray-600 mb-4">
            Tu contraseña ha sido cambiada exitosamente. Ya puedes iniciar
            sesión con tu nueva contraseña.
          </p>
          <Button
            onClick={() => navigate("/auth")}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            Ir al inicio de sesión
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Fracto
          </h2>
          <p className="mt-2 text-sm text-gray-600">Cambiar contraseña</p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <Label htmlFor="email" className="text-gray-700 font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                placeholder="tu@email.com"
                {...register("email")}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="codigo" className="text-gray-700 font-medium">
                Código de Recuperación
              </Label>
              <div className="flex gap-2">
                <Input
                  id="codigo"
                  type="text"
                  required
                  className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                  placeholder="123456"
                  {...register("codigo")}
                />
                <Button
                  type="button"
                  onClick={handleVerifyCode}
                  disabled={verifyingCode || !email || !codigo}
                  className="mt-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl disabled:opacity-50"
                >
                  {verifyingCode ? "Verificando..." : "Verificar"}
                </Button>
              </div>
              {errors.codigo && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.codigo.message}
                </p>
              )}
              <p className="mt-2 text-sm text-gray-500">
                Ingresa el código que recibiste por email.
              </p>
            </div>

            <div>
              <Label
                htmlFor="nuevaPassword"
                className="text-gray-700 font-medium"
              >
                Nueva Contraseña
              </Label>
              <Input
                id="nuevaPassword"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                placeholder="Mínimo 6 caracteres"
                {...register("nuevaPassword")}
              />
              {errors.nuevaPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.nuevaPassword.message}
                </p>
              )}
            </div>

            <div>
              <Label
                htmlFor="confirmPassword"
                className="text-gray-700 font-medium"
              >
                Confirmar Nueva Contraseña
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                placeholder="Repite tu nueva contraseña"
                {...register("confirmPassword")}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            <div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-200 transform hover:scale-105"
              >
                {isSubmitting
                  ? "Cambiando contraseña..."
                  : "Cambiar contraseña"}
              </Button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate("/auth")}
                className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
              >
                Volver al inicio de sesión
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
